import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import "firebase/compat/storage";
import { InstagramPost } from "../models/instagram-post.model";
import { CasingUtils } from "../utils/casing.utils";
import EnvironmentUtils from "../utils/environment.utils";

const config = {
  apiKey: EnvironmentUtils.FIREBASE_API_KEY,
  authDomain: EnvironmentUtils.FIREBASE_AUTH_DOMAIN,
  databaseURL: EnvironmentUtils.FIREBASE_DATABASE_URL,
  projectId: EnvironmentUtils.FIREBASE_PROJECT_ID,
  storageBucket: EnvironmentUtils.FIREBASE_STORAGE_BUCKET,
  messagingSenderId: EnvironmentUtils.FIREBASE_MESSAGING_SENDER_ID,
  appId: EnvironmentUtils.FIREBASE_APP_ID,
};

if (!firebase.apps.length) {
  firebase.initializeApp(config);
}

export const auth = firebase.auth();
export const firestore = firebase.firestore();
export const storageRef = firebase.storage().ref();

type FirebaseDoc = firebase.firestore.DocumentData;

export class FirestoreService {
  async getLatestInstagramPosts(postCount: number): Promise<InstagramPost[]> {
    const safePostCount = postCount > 0 ? postCount : 1;

    const snapshot = await firestore
      .collection("instagram_posts")
      .withConverter(this._createFirestoreConverter<InstagramPost>())
      .where("display", "==", true)
      .orderBy("priority", "desc")
      .orderBy("timestamp", "desc")
      .limit(safePostCount)
      .get();

    return snapshot.docs.map((doc) => doc.data() as InstagramPost);
  }

  private _createFirestoreConverter<
    T = FirebaseDoc,
  >(): firebase.firestore.FirestoreDataConverter<T> {
    const self = this;
    return {
      toFirestore(data: T): FirebaseDoc {
        return data as FirebaseDoc;
      },
      fromFirestore(
        snapshot: firebase.firestore.QueryDocumentSnapshot<FirebaseDoc>,
      ): T {
        const data = snapshot.data();
        const withDatesConverted = self._convertTimestamps(data);
        const withCamelCaseKeys =
          CasingUtils.convertKeysToCamelCase(withDatesConverted);
        return { id: snapshot.id, ...withCamelCaseKeys } as T;
      },
    };
  }

  private _convertTimestamps(obj: any): any {
    if (obj === null || obj === undefined) return obj;

    if (obj instanceof firebase.firestore.Timestamp) {
      return obj.toDate();
    }

    if (obj instanceof Date) {
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(this._convertTimestamps);
    }

    if (typeof obj === "object") {
      const converted: Record<string, any> = {};
      for (const [key, value] of Object.entries(obj)) {
        converted[key] = this._convertTimestamps(value);
      }
      return converted;
    }

    return obj;
  }
}

export const firestoreService = new FirestoreService();

export default firebase;
